import { EpgApi } from '../../api/EpgApi';
import { ActionMethod, AssetType, CardType, SortDir } from '../../api/models/Shared.types';
import { EpgUtil } from '../utility/EpgUtil';
import { CardSizeInfo } from '../cms/CardSizeInfo';
import { CardDataSource } from '../cms/CardDataSource';
import { CmsRibbon } from '../../components/cms/CmsRibbon';
import { Theme } from '../../config/theme';
import { Backend } from '../../api/Backend';
import { ChannelModel } from '../../api/EpgApi.types';
import {
  IFavoriteElementV1,
  IRibbon2Asset,
  IRibbon2Detail,
  ISchedule,
  IScheduleSummary,
  IScheduleTime,
} from '../../api/models/AndromeTV.types';

const _constants = {
  firstLoadAmount: 6,
  loadAmount: 3,
};

export interface ListRibbon2Detail extends IRibbon2Detail {
  tags?: string[];
  published: IScheduleTime;
}

export interface ListRibbon2Asset extends IRibbon2Asset {
  asset: ListRibbon2Detail;
}

export class ListRibbonDataSource {
  public backend: Backend;
  private channel: ChannelModel;
  private from: Date;
  private until: Date;
  private replay: boolean;
  private categories: string[];
  private properties: string[];
  private channels: ChannelModel[];
  private title: string;
  private isChannel: boolean;
  private hasNextPage: boolean;
  private sizeInfo: CardSizeInfo;
  private cards: CardDataSource[];
  private loadedIdx: number;
  private favorite: Promise<IFavoriteElementV1 | undefined> | undefined;
  private loaded: boolean;

  constructor(
    backend: Backend,
    channel: ChannelModel,
    from: Date,
    until: Date,
    replay = false,
    categories: string[],
    properties: string[],
    channels: ChannelModel[],
    title: string,
    isChannel = false
  ) {
    this.backend = backend;
    this.channel = channel;
    this.from = from;
    this.until = until;
    this.replay = replay;
    this.categories = categories;
    this.properties = properties;
    this.channels = channels;
    this.title = title;
    this.isChannel = isChannel;

    this.hasNextPage = true;
    this.sizeInfo = new CardSizeInfo();
    this.sizeInfo.setToListPageSettings();
    this.cards = [];
    // console.log('ListRibbonDataSource', properties);
    this.loadedIdx = 0;
    this.loaded = false;
  }

  load() {
    return EpgApi.scheduleForChannel(this.backend, this.channel.channelId, this.from, this.until, this.categories).then(
      result => {
        if (!result.schedules) {
          return [];
        }

        if (this.replay) {
          EpgUtil.sortSchedulesByStartDate(result.schedules, SortDir.Desc);
          const now = new Date().getTime();
          result.schedules = result.schedules.filter(s => +s.published.end * 1000 < now);
        } else {
          EpgUtil.sortSchedulesByStartDate(result.schedules, SortDir.Asc);
          const now = new Date().getTime();
          result.schedules = result.schedules.filter(s => +s.published.end * 1000 > now);
        }
        if (this.isChannel) {
          this.favorite = this.backend.cachedFavoriteApi.getFavorite(this.backend, {
            id: this.channel.channelId,
            type: AssetType.CHANNEL,
          });
        }

        const assets = result.schedules
          .map(schedule => this.scheduleToRibbon2Asset(schedule, result))
          .filter(schedule => schedule);

        const noUndef: ListRibbon2Asset[] = assets.filter(el => el) as ListRibbon2Asset[];
        this.sizeInfo.updateWithContainer(
          {
            type: CardType.Landscape,
            nextPage: false,
            items: noUndef,
          },
          this.properties
        );

        this.cards = noUndef.map(
          item => new CardDataSource(this.backend, item, this.sizeInfo, this.properties, this.channels)
        );
        this.hasNextPage = false;
        this.loaded = true;
        return this.cards;
      }
    );
  }

  loadNext() {
    let numberToLoad: number;
    if (this.loadedIdx === 0) {
      numberToLoad = _constants.firstLoadAmount;
    } else {
      numberToLoad = _constants.loadAmount;
    }
    if (this.loadedIdx >= this.cards.length) {
      return new Promise(resolve => {
        resolve([]);
      });
    } else {
      return new Promise(resolve => {
        const result = this.cards.slice(this.loadedIdx, this.loadedIdx + numberToLoad);
        this.loadedIdx += numberToLoad;
        resolve(result);
      });
    }
  }

  hasNext() {
    return this.loadedIdx < this.cards.length || this.hasNextPage;
  }

  get isLoaded() {
    return this.loaded;
  }

  scheduleToRibbon2Asset(schedule: IScheduleSummary, scheduleForChannel: ISchedule): ListRibbon2Asset | undefined {
    const program = scheduleForChannel.programs?.find(p => p.id === schedule.program);

    if (!program) {
      return undefined;
    }

    const resources: IRibbon2Detail[] = [];
    scheduleForChannel.programs?.forEach(program =>
      resources.push(EpgUtil.programToRibbon2Detail(program, scheduleForChannel.seasons))
    );
    // console.log('ListRibbon', this, schedule, program, scheduleForChannel);
    const season = scheduleForChannel.seasons?.find(obj => {
      return obj.id === program.season;
    });
    return {
      asset: {
        ...program,
        title: program.title,
        synopsis: program.shortSynopsis,
        image: program.posterImage!,
        genres: program.genres,
        episode: program.episode,
        season: season?.season,
        published: schedule.published,
        type: AssetType.SCHEDULE,
        id: schedule.id,
      },
      method: ActionMethod.DETAIL,
      channels: [
        {
          id: this.channel.channelId,
          schedules: [
            {
              ...schedule,
              programId: schedule.program,
            },
          ],
        },
      ],
      resources,
    };
  }

  isEmpty() {
    return this.cards.length === 0;
  }

  totalHeight() {
    return Theme.Fonts.Ribbon.Title.lineHeight + this.sizeInfo.height;
  }

  height() {
    return this.totalHeight();
  }

  componentDescription() {
    return {
      focusable: true,
      type: CmsRibbon,
      data: this,
      h: this.totalHeight(),
    };
  }
}
